import { useState } from 'react';
import ContentCopy from '@mui/icons-material/ContentCopy';
import { Box, IconButton, Button, Tooltip, Typography } from '@mui/material';
import { useLocalization } from '@/hooks/useLocalization';
import { INVITATION_TEMPLATE_VARIABLES } from '../constants/invitationTemplateVariables';

type InvitationTemplateVariableListProps = {
  onInsert: (token: string) => void;
};

const InvitationTemplateVariableList = ({
  onInsert,
}: InvitationTemplateVariableListProps) => {
  const { t } = useLocalization();
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const handleCopy = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      setCopiedToken(token);
      window.setTimeout(() => {
        setCopiedToken((current) => (current === token ? null : current));
      }, 1500);
    } catch {
      // Clipboard may be unavailable in some embedded contexts.
    }
  };

  return (
    <Box
      className='wa-invite-vars__list'
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr) auto',
          sm: 'max-content 40px minmax(0, 1fr)',
        },
        columnGap: { xs: 1, sm: 2 },
        rowGap: 1.25,
        alignItems: 'center',
        width: '100%',
      }}
    >
      {INVITATION_TEMPLATE_VARIABLES.map(
        ({ token, descriptionKey, defaultDescription }) => (
          <Box
            key={token}
            sx={{
              display: 'contents',
            }}
          >
            <Tooltip
              title={t('bulkInvite.variableActions.insert', {
                defaultValue: 'Insert {{token}} into message',
                token,
              })}
            >
              <Button
                size='small'
                variant='text'
                onClick={() => {
                  onInsert(token);
                }}
                className='wa-invite-vars__code'
                sx={{
                  fontFamily: 'monospace',
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  minWidth: 0,
                  px: 0.5,
                  py: 0.25,
                  gridColumn: { xs: '1', sm: '1' },
                }}
              >
                {token}
              </Button>
            </Tooltip>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gridColumn: { xs: '2', sm: '2' },
              }}
            >
              <Tooltip
                title={
                  copiedToken === token
                    ? t('bulkInvite.variableActions.copied', {
                        defaultValue: 'Copied {{token}}',
                        token,
                      })
                    : t('bulkInvite.variableActions.copy', {
                        defaultValue: 'Copy {{token}}',
                        token,
                      })
                }
              >
                <IconButton
                  size='small'
                  aria-label={t('bulkInvite.variableActions.copy', {
                    defaultValue: 'Copy {{token}}',
                    token,
                  })}
                  onClick={() => {
                    void handleCopy(token);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <ContentCopy sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>

            <Typography
              variant='body2'
              color='text.secondary'
              className='wa-invite-vars__desc'
              sx={{
                gridColumn: { xs: '1 / -1', sm: '3' },
                pl: { xs: 0.5, sm: 0 },
                pb: { xs: 0.5, sm: 0 },
              }}
            >
              {t(descriptionKey, defaultDescription)}
            </Typography>
          </Box>
        ),
      )}
    </Box>
  );
};

export default InvitationTemplateVariableList;
